import React, { useState } from 'react';
import { FileText, Activity, BookOpen, MessageCircle, ArrowRight } from 'lucide-react';

interface PortalTab {
  id: string;
  icon: React.ReactNode;
  label: string;
  description: string;
}

export const PatientPortal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('notes');
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);

  const tabs: PortalTab[] = [
    { id: 'notes', icon: <FileText size={20} />, label: 'Visit Notes', description: 'Your recent visit summaries' },
    { id: 'results', icon: <Activity size={20} />, label: 'Test Results', description: 'Lab and imaging results' },
    { id: 'education', icon: <BookOpen size={20} />, label: 'Education', description: 'Health information' },
    { id: 'messages', icon: <MessageCircle size={20} />, label: 'Messages', description: 'Contact your care team' }
  ];

  const mockNotes = [
    {
      id: 'note-1',
      date: 'Today at 2:30 PM',
      provider: 'Dr. Sarah Chen',
      title: 'Blood Sugar Management Follow-up',
      summary: 'Discussed elevated glucose readings. Adjusting metformin dose.',
      fullNote: `Today\'s Visit Summary:

You came in for a follow-up on your diabetes management. Your recent glucose readings have been running 140-160 mg/dL at home, which is a bit higher than our target.

What we discussed:
• Your current metformin dose (1000mg) may need adjustment
• The importance of consistent meal timing and portion control
• How to use your home glucose monitor

What we\'re doing:
• Increasing your metformin to 1500mg daily
• Scheduling follow-up labs in 4 weeks
• Recommending a session with our diabetes educator

Next steps:
• Take your new metformin dose as directed
• Keep track of your glucose readings (we can see them in your patient portal)
• Contact us if you have questions or concerns

This visit included: Extended history and physical exam, lab review, medication adjustment.`
    },
    {
      id: 'note-2',
      date: 'April 15, 2026 at 10:15 AM',
      provider: 'Dr. Michael Torres',
      title: 'Annual Physical',
      summary: 'Overall health check. Some labs need follow-up.',
      fullNote: 'Your annual physical was completed. All vital signs are stable. Some lab values need monitoring...'
    },
    {
      id: 'note-3',
      date: 'April 1, 2026 at 3:45 PM',
      provider: 'Dr. Jennifer Liu',
      title: 'Kidney Function Review',
      summary: 'Reviewing CKD progression. Medication adjustment planned.',
      fullNote: 'Your kidney function labs show some progression...'
    }
  ];

  const mockResults = [
    { name: 'Glucose', value: '145 mg/dL', range: '70-100', status: 'high', date: 'Today' },
    { name: 'Creatinine', value: '1.8 mg/dL', range: '0.7-1.3', status: 'high', date: 'Today' },
    { name: 'eGFR', value: '42 mL/min', range: '>60', status: 'low', date: 'Today' },
    { name: 'Hemoglobin A1C', value: '8.8%', range: '<7%', status: 'high', date: 'April 10' }
  ];

  const mockEducation = [
    { title: 'Managing Blood Sugar at Home', icon: '📊', tags: ['Diabetes', 'Beginner'] },
    { title: 'Kidney Health and Your Diet', icon: '🥗', tags: ['CKD', 'Nutrition'] },
    { title: 'Medication Side Effects & How to Manage Them', icon: '💊', tags: ['Medications'] },
    { title: 'Exercise for People with Kidney Disease', icon: '🏃', tags: ['CKD', 'Exercise'] }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Patient Portal (Phase 3)</h2>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
          👥 Patient Engagement
        </span>
      </div>

      {/* Tab Navigation */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`p-3 rounded-lg border transition-all text-left ${
              activeTab === tab.id
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">{tab.icon}</div>
            <p className="text-sm font-semibold">{tab.label}</p>
            <p className="text-xs opacity-75">{tab.description}</p>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="min-h-96">
        {/* Visit Notes */}
        {activeTab === 'notes' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <p className="text-sm text-green-800">✓ All visit notes are available same-day after your appointment</p>
            </div>
            {mockNotes.map((note) => (
              <div key={note.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                <button
                  onClick={() => setExpandedNoteId(expandedNoteId === note.id ? null : note.id)}
                  className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{note.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{note.summary}</p>
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span>{note.provider}</span>
                        <span>{note.date}</span>
                      </div>
                    </div>
                    <ArrowRight className={`text-gray-400 transition-transform ${expandedNoteId === note.id ? 'rotate-90' : ''}`} size={20} />
                  </div>
                </button>

                {expandedNoteId === note.id && (
                  <div className="bg-gray-50 border-t border-gray-200 p-4">
                    <p className="text-sm text-gray-700 whitespace-pre-line">{note.fullNote}</p>
                    <button className="mt-4 text-indigo-600 font-semibold text-sm hover:text-indigo-700">
                      → Ask provider a question
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Test Results */}
        {activeTab === 'results' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="text-sm text-blue-800">Test results are available immediately after processing</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-900">Test</th>
                    <th className="text-right p-3 font-semibold text-gray-900">Result</th>
                    <th className="text-right p-3 font-semibold text-gray-900">Normal Range</th>
                    <th className="text-center p-3 font-semibold text-gray-900">Status</th>
                    <th className="text-right p-3 font-semibold text-gray-900">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {mockResults.map((result, idx) => (
                    <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-3 font-medium text-gray-900">{result.name}</td>
                      <td className="p-3 text-right text-gray-700">{result.value}</td>
                      <td className="p-3 text-right text-gray-600">{result.range}</td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          result.status === 'high' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {result.status}
                        </span>
                      </td>
                      <td className="p-3 text-right text-gray-600">{result.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Health Education */}
        {activeTab === 'education' && (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <p className="text-sm text-purple-800">Personalized education based on your conditions</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mockEducation.map((item, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
                  <p className="text-2xl mb-2">{item.icon}</p>
                  <p className="font-semibold text-gray-900 mb-2">{item.title}</p>
                  <div className="flex gap-2">
                    {item.tags.map((tag) => (
                      <span key={tag} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {activeTab === 'messages' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="text-sm text-blue-800">Message your care team. Responses typically within 24 hours.</p>
            </div>
            <textarea
              placeholder="Ask your provider a question or request a follow-up..."
              className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={6}
            />
            <button className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-indigo-700">
              Send Message
            </button>
          </div>
        )}
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
        <div className="text-center">
          <p className="text-xl font-bold text-green-600">100%</p>
          <p className="text-xs text-gray-600">Same-day notes</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-blue-600">50%</p>
          <p className="text-xs text-gray-600">Portal engagement</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-purple-600">75%</p>
          <p className="text-xs text-gray-600">Satisfaction</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-orange-600">24h</p>
          <p className="text-xs text-gray-600">Message response</p>
        </div>
      </div>
    </div>
  );
};
